import api from './api';
import router from '../../router';
import i18n from '../../plugins/i18n.js';

//Path Regex to check if socket listener should be used
const pathRegex = /^\/(users|planning|userGroups)(\/|$)/;

// Helper to handle API errors consistently
function handleApiError(commit, err) {
	let message;

	if (err.response?.status === 423) {
		const { i18nKey, i18nParams } = err.response.data;
		if (i18nKey) {
			message = i18n.t(i18nKey, i18nParams);
		} else {
			message = err.response?.data.message;
		}
	} else {
		message = err.response?.data.message;
	}

	commit('setError', { message });
}

const state = {
	users: [],
	usersMap: {},
	supervisors: [],
	absences: {},
	roles: [],
	preferences: {},
	statistics: {},
	loading: false,
	error: false,
	message: '',
	showDeleted: false,
	deletedAbsenceIDs: [],
	publishedMetRatio: 0,
	metRatioDebounce: 0,
	manualWorktimeBalances: [],
	lastStatisticsByUser: {},
};

const getters = {
	getUserById: (state) => (userId) => {
		return state.usersMap[userId];
	},
};

const mutations = {
	startLoading(state) {
		state.loading = true;
	},
	stopLoading(state) {
		state.loading = false;
		state.error = false;
	},
	setShowDeleted(state, value) {
		state.showDeleted = Boolean(value);
	},
	setUsers(state, { users }) {
		users.forEach((user) => {
			user.initials = user.firstName.substr(0, 2) + user.lastName.substr(0, 1);
			user.online = false;
		});
		state.users = users; // Users are sorted by the database (sort key defined in api request)
		// state.users = users.sort(function(a, b) {
		// 	return a.lastName.localeCompare(b.lastName);
		// });
		state.usersMap = users.reduce((acc, user) => {
			acc[user['_id']] = user;
			return acc;
		}, {});
	},
	setAbsences(state, { absences }) {
		const currentAbsences = JSON.parse(JSON.stringify(state.absences));

		absences.forEach((absence) => {
			let driverId = absence.user;
			let absenceObj = {};

			if (currentAbsences[driverId]) {
				absenceObj = currentAbsences[driverId];
			}

			absenceObj[absence.id] = absence;
			currentAbsences[driverId] = absenceObj;
		});

		state.absences = currentAbsences;
	},
	setPreferences(state, { preferences }) {
		const currentPreferences = JSON.parse(JSON.stringify(state.preferences));
		preferences.forEach((preference) => {
			let driverId = preference.user;

			currentPreferences[driverId] = preference;
		});

		state.preferences = currentPreferences;
	},
	setStatistics(state, { statistics }) {
		const currentStats = JSON.parse(JSON.stringify(state.statistics));
		statistics.forEach((stats) => {
			let driverId = stats.user;

			currentStats[driverId] = stats;
		});

		state.statistics = currentStats;
	},
	setError(state, { message }) {
		(state.error = true), (state.message = message);
	},
	resetError(state) {
		(state.error = false), (state.message = '');
	},
	setSupervisors(state, supervisors) {
		state.supervisors = supervisors;
	},
	addDeletedAbsence(state, deletedAbsenceID) {
		state.deletedAbsenceIDs.push(deletedAbsenceID);
	},
	removeDeletedAbsence(state, deletedAbsenceID) {
		const index = state.deletedAbsenceIDs.indexOf(deletedAbsenceID);
		if (index > -1) {
			state.deletedAbsenceIDs.splice(index, 1);
		}
	},
	setPublishedMetRatio(state, publishedMetRatio) {
		state.publishedMetRatio = publishedMetRatio;
	},
	setMetRatioDebounce(state, debounce) {
		state.metRatioDebounce = debounce;
	},
	setOnlineUsers(state, { onlineUsers }) {
		state.users.forEach((user) => {
			user.online = onlineUsers[user.id] == true;
		});
	},
	setUserOnline(state, { userId, online }) {
		if (state.users && state.users.length > 0) {
			const user = state.users.find((user) => user._id == userId);
			if (user) {
				user.online = online;
			}
		}
	},
	setManualWorktimeBalances(state, manualWorktimeBalances) {
		state.manualWorktimeBalances = manualWorktimeBalances;
	},
	setLastStatisticsByUser(state, statistics) {
		state.lastStatisticsByUser = statistics;
	},
	clearError(state) {
		(state.error = false), (state.message = '');
	},
};

const actions = {
	async load({ state, commit, dispatch, rootGetters }, showDeleted) {
		const normalized = showDeleted ?? state.showDeleted;

		commit('startLoading');
		try {
			let users = state.users;

			let response = await api.list(normalized);
			users = response.data;
			commit('setUsers', { users });

			response = await api.getOnlineUsers();
			if (response && response.data) {
				commit('setOnlineUsers', { onlineUsers: response.data });
			}

			commit('stopLoading');
			return users;
		} catch (err) {
			commit('setError', { message: err.response.data.message });
			commit('stopLoading');
		}
	},

	socket_created_User({ dispatch, commit }, payload) {
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}

		dispatch('load', state.showDeleted);
	},

	socket_updated_User({ dispatch, commit }, payload) {
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}

		dispatch('load', state.showDeleted);
	},

	socket_deleted_User({ dispatch, commit }, payload) {
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}

		dispatch('load', state.showDeleted);
	},

	loadAbsences({ state, commit }, id) {
		return api
			.allAbsences(id)
			.then((response) => {
				const absences = response.data;
				commit('setAbsences', { absences });
				commit('stopLoading');
				return state.absences;
			})
			.catch((err) => {
				commit('setError', { message: err.response.data.message });
				commit('stopLoading');
			});
	},

	async checkAbsenceOverlap({ state, commit }, absence) {
		commit('clearError');
		try {
			if (absence?.new) {
				delete absence.id;
			}
			const response = await api.absenceOverlap(absence);
			return response.data;
		} catch (err) {
			commit('setError', { message: err.response.data.message });
		}
	},

	async loadManualBalances({ state, commit }, { user, month }) {
		try {
			let result = await api.getSingleUserManualWorktimeBalances(user, month);
			if (result) {
				result = result.data.map(({ type, deleted, user, day, amount, id, reason }) => ({
					type,
					deleted,
					user,
					day: day,
					amount: amount, // Convert to minutes
					id,
					reason,
				}));

				commit('setManualWorktimeBalances', result);
				return state.manualWorktimeBalances;
			}
		} catch (err) {
			commit('setError', { message: err.response.data.message });
		}
	},
	async createWorktimeBalance({ commit, dispatch }, balance) {
		try {
			const response = await api.createWorktimeBalances(balance);

			dispatch('loadManualBalances', { user: balance.user });
			return response;
		} catch (err) {
			handleApiError(commit, err);
		}
	},
	async updateWorktimeBalance({ commit, dispatch }, balance) {
		try {
			const response = await api.updateWorktimeBalance(balance);
			dispatch('loadManualBalances', { user: balance.user });
			return response;
		} catch (err) {
			handleApiError(commit, err);
		}
	},
	async deleteWorktimeBalance({ commit, dispatch }, balance) {
		try {
			const response = await api.deleteWorktimeBalance(balance);
			dispatch('loadManualBalances', { user: balance.user });
			return response;
		} catch (err) {
			commit('setError', { message: err.response?.data.message });
		}
	},

	loadPreferences({ state, commit }) {
		return api
			.allPreferences()
			.then((response) => {
				const preferences = response.data;
				commit('setPreferences', { preferences });
				commit('stopLoading');
				return state.preferences;
			})
			.catch((err) => {
				commit('setError', { message: err.response.data.message });
				commit('stopLoading');
			});
	},

	async loadStatistics({ state, commit }, { user, date }) {
		try {
			const yesterdayStatistics = await api.getStatistics(user, date);
			const lastStatisticsOfYear = await api.getLastStatistics(user, new Date(date).getFullYear());
			const publishedMetRatio = (await api.getPublishedMetRatio(user)).data;
			const statistics = yesterdayStatistics.data;
			if (!statistics.length) {
				statistics.push({ user });
			}

			// Fallback to 0 to avoid NaN when statistics data is missing
			const { vacationBalance = 0, yearlyUsedVacation = 0, restDayBalance = 0 } = lastStatisticsOfYear?.data ?? {};
			const { yearlyUsedVacation: usedVacation = 0 } = statistics[0] ?? {};

			statistics[0].totalVacationForYear = vacationBalance + yearlyUsedVacation;
			statistics[0].yearlyVacationBalance = statistics[0].totalVacationForYear - usedVacation;
			statistics[0].yearlyRestDayBalance = restDayBalance;
			commit('setStatistics', { statistics });
			commit('setPublishedMetRatio', publishedMetRatio);
			commit('stopLoading');
			return state.statistics;
		} catch (err) {
			console.log(err);
			const message = err?.response?.data?.message || err?.message;
			commit('setError', { message });
			commit('stopLoading');
		}
	},

	async loadSupervisors({ state, commit }) {
		const result = await api.listSupervisors();
		commit('setSupervisors', result.data);
	},
	async addNewlyDeletedAbsence({ state, commit }, deletedAbsenceID) {
		commit('addDeletedAbsence', deletedAbsenceID);
	},

	async socket_update_Day({ dispatch, state, commit }, payload) {
		if (!pathRegex.test(router.currentRoute._value.path)) {
			return;
		}

		clearTimeout(state.metRatioDebounce);
		const timeout = setTimeout(async () => {
			if (router.currentRoute.params?.id) {
				const publishedMetRatio = (await api.getPublishedMetRatio(router.currentRoute.params.id)).data;
				commit('setPublishedMetRatio', publishedMetRatio);
			}
		}, 100);

		commit('setMetRatioDebounce', timeout);
	},

	async socket_userConnected({ dispatch, state, commit }, payload) {
		const userId = payload[0].payload;
		commit('setUserOnline', { userId, online: true });
	},

	async socket_userDisconnected({ dispatch, state, commit }, payload) {
		const userId = payload[0].payload;
		commit('setUserOnline', { userId, online: false });
	},
	async loadLastStatisticsByUser({ commit }, { user, year }) {
		const result = await api.getLastStatistics(user, year);
		commit('setLastStatisticsByUser', result.data);
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
